import React from "react";
import { tokens } from "../../Helpers/tokens";
import "./Badge.scss";

const hexToRgbaWithOpacity = (hex, opacity = 0.1) => {
  hex = hex.replace("#", "");

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Componente Badge - Etiqueta visual para categorizar contenido
 * @param {Object} props - Props del componente
 * @param {string} [props.id] - Identificador único del badge
 * @param {string} [props.textColor] - Color del texto personalizado (desde backend)
 * @param {string} [props.backgroundColor] - Color de fondo personalizado (desde backend)
 * @param {string} [props.borderColor] - Color del borde personalizado (desde backend)
 * @param {"default"|"filter"} [props.variant="default"] - Variante del badge
 * @param {boolean} [props.selected=false] - Estado de selección (solo para variant="filter")
 * @param {boolean} [props.disabled=false] - Estado deshabilitado del badge
 * @param {function} [props.onClick] - Función que se ejecuta al hacer click
 * @param {React.ReactNode} props.children - Contenido del badge
 * @param {string} [props.className=""] - Clases CSS adicionales
 * @returns {JSX.Element} Elemento Badge
 */
export const Badge = ({
  id,
  textColor,
  backgroundColor,
  borderColor,
  variant = "default",
  selected = false,
  disabled = false,
  onClick,
  children,
  className = "",
  ...props
}) => {
  const getColors = () => {
    const shouldShowColors =
      variant === "default" || (variant === "filter" && selected);

    if (shouldShowColors) {
      let processedBackgroundColor = backgroundColor || "#e4e4e4";

      if (backgroundColor && backgroundColor.startsWith("#")) {
        processedBackgroundColor = hexToRgbaWithOpacity(backgroundColor);
      }

      return {
        color: textColor || tokens.color("azul-7"),
        backgroundColor: processedBackgroundColor,
        borderColor: borderColor || textColor || tokens.color("gris-acero"),
      };
    }

    return {
      color: tokens.color("azul-7"),
      backgroundColor: "transparent",
      borderColor: tokens.color("gris-1-5"),
    };
  };

  const colors = getColors();

  const badgeClasses = [
    "custom-badge",
    variant === "filter" ? "badge-filter" : "badge-normal",
    disabled && "badge-disabled",
    onClick && "badge-clickable",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const badgeStyle = {
    color: colors.color,
    backgroundColor: colors.backgroundColor,
    borderColor: colors.borderColor,
  };

  return (
    <span
      id={id}
      className={badgeClasses}
      style={badgeStyle}
      onClick={disabled ? undefined : onClick}
      {...props}
    >
      {children}
    </span>
  );
};
